import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  MdQrCode2,
  MdPayments,
  MdCheckCircle,
  MdArrowForward,
  MdPerson,
  MdTrain,
  MdLuggage,
  MdReceiptLong,
  MdOpenInNew,
  MdAdd,
  MdArrowBack,
} from "react-icons/md";
import CrudPage from "components/CrudPage/CrudPage";
import { snack } from "components/Snack/Snack";
import { useGetTrains } from "hooks/useGetTrains";
import { useGetStations } from "hooks/useGetStations";
import { useGetCustomers } from "hooks/useGetCustomers";
import baggageTripsService from "services/admin/baggageTripsService";
import customersService from "services/admin/customersService";
import counterpartiesService from "services/admin/counterpartiesService";
import { BAGGAGE_TYPE_OPTIONS } from "pages/Admin/Baggages/Baggage/baggage-status";
import PageTitle from "components/PageTitle/PageTitle";

const STEPS = ["Клиент и маршрут", "Детали груза", "Оплата"];

const today = () => new Date().toISOString().slice(0, 10);

const initialForm = () => ({
  clientType: "individual",
  customerID: null as string | null,
  counterpartyID: null as number | null,
  hasRecipient: false,
  recipientName: "",
  recipientPhone: "",
  trainID: "" as any,
  originStationID: "" as any,
  destinationStationID: "" as any,
  departureDate: today(),
  ticketNumber: "",
  // груз
  weight: "" as any,
  quantity: 1 as any,
  type: "Чемодан",
  declaredValueAmount: "" as any,
  description: "",
  // оплата
  paymentMethod: "kaspi",
});

const customerName = (c: any) =>
  c ? `${c?.lastName || ""} ${c?.firstName || ""}`.trim() || c?.name || "—" : "—";

const customerFilter = createFilterOptions<any>();

const emptyNewClient = () => ({ open: false, lastName: "", firstName: "", iin: "", phone: "" });

const money = (v: any, currency: string) => `${Number(v || 0).toLocaleString("ru-RU")} ${currency}`;

/** Заголовок секции внутри карточки шага — единый корпоративный стиль (как на детальной). */
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    sx={{ display: "block", letterSpacing: 0.6, fontWeight: 700 }}
  >
    {children}
  </Typography>
);

/** Строка «иконка — подпись — значение» для боковой сводки заказа. */
const SummaryLine = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
    <Box sx={{ color: "text.secondary", display: "flex", fontSize: 18 }}>{icon}</Box>
    <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}
    >
      {value}
    </Typography>
  </Box>
);

/** Липкая боковая сводка заказа: маршрут, клиент, груз и расчёт тарифа. */
const OrderSummary = ({
  originName,
  destName,
  customerLabel,
  trainLabel,
  weight,
  qty,
  breakdown,
  hasTicket,
}: any) => {
  const currency = breakdown?.currency || "KZT";
  const hasPrice = breakdown && breakdown.total > 0;

  const PriceRow = ({
    label,
    value,
    strong,
  }: {
    label: string;
    value: string;
    strong?: boolean;
  }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.25 }}>
      <Typography variant="body2" color={strong ? "text.primary" : "text.secondary"}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 500 }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, position: "sticky", top: 16 }}>
      <CardContent>
        <SectionTitle>Сводка заказа</SectionTitle>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Откуда
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {originName || "—"}
            </Typography>
          </Box>
          <Box sx={{ color: "primary.main", display: "flex", fontSize: 22 }}>
            <MdArrowForward />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Куда
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {destName || "—"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <SummaryLine icon={<MdPerson />} label="Клиент" value={customerLabel} />
        {trainLabel ? <SummaryLine icon={<MdTrain />} label="Поезд" value={trainLabel} /> : null}
        {weight > 0 ? (
          <SummaryLine icon={<MdLuggage />} label="Груз" value={`${qty} мест · ${weight} кг`} />
        ) : null}

        <Divider sx={{ my: 1.5 }} />

        {hasTicket && (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label="Применена скидка 50% по билету"
            sx={{ mb: 1, fontWeight: 600 }}
          />
        )}

        {!hasPrice ? (
          <Typography variant="body2" color="text.secondary">
            Укажите вес и количество мест на шаге «Детали груза» для расчёта стоимости.
          </Typography>
        ) : (
          <>
            <PriceRow
              label={`Перевозка (${breakdown.blocks} × 10 кг)`}
              value={money(breakdown.carriage, currency)}
            />
            {breakdown.discountAmount > 0 && (
              <PriceRow
                label={`Скидка по билету (−${breakdown.discountPercent}%)`}
                value={`−${money(breakdown.discountAmount, currency)}`}
              />
            )}
            {breakdown.declaredValueFee > 0 && (
              <PriceRow
                label="Объявленная ценность"
                value={money(breakdown.declaredValueFee, currency)}
              />
            )}
            <PriceRow label="Распечатка накладной" value={money(breakdown.waybillFee, currency)} />
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <Typography variant="body2" color="text.secondary">
                Итого
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {money(breakdown.total, currency)}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const BaggageTripWizard = () => {
  const navigate = useNavigate();
  const orgID = localStorage.getItem("orgID") || "";

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(initialForm());
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [createdTrip, setCreatedTrip] = useState<any>(null);
  const [newClient, setNewClient] = useState(emptyNewClient());
  const [creatingClient, setCreatingClient] = useState(false);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const { data: trainsResponse } = useGetTrains({ orgID });
  const { data: stationsResponse } = useGetStations({ orgID });
  const { data: customersResponse } = useGetCustomers({ orgID, query: customerSearch });
  // Контрагенты (юр. лица) — только активные; реестр ведётся в разделе «Контрагенты».
  const { data: counterpartiesResponse } = useQuery({
    queryKey: ["baggage-counterparties", { orgID, active: 1 }],
    queryFn: async () => {
      const res: any = await counterpartiesService.getAll({ active: 1 });
      return res.data;
    },
  });

  const trains = (trainsResponse as any)?.data || [];
  const stations = (stationsResponse as any)?.data || [];
  const customers = (customersResponse as any)?.data?.rows || [];
  const counterpartyOptions = ((counterpartiesResponse as any)?.data || []).filter(
    (c: any) => c.isActive,
  );
  const selectedCounterparty =
    counterpartyOptions.find((c: any) => c.id === form.counterpartyID) || null;
  const counterpartyLabel = (c: any) =>
    c ? `${c.name}${c.bin ? ` (БИН ${c.bin})` : ""}` : "";

  const stationOptions = stations.map((s: any) => ({ id: s.id, name: s.name }));

  // Выбранный клиент мог не попасть в результаты поиска — подмешиваем его в опции.
  const customerOptions =
    selectedCustomer && !customers.find((c: any) => c.id === selectedCustomer.id)
      ? [selectedCustomer, ...customers]
      : customers;

  // Производные значения для расчёта
  const weightNum = Number(form.weight) || 0;
  const qtyNum = Math.max(1, Math.floor(Number(form.quantity) || 1));
  const declaredValue = Number(form.declaredValueAmount) > 0;
  // Скидка по билету — розничная льгота физлица; у юр. лица тариф договорной.
  const hasTicket =
    form.clientType !== "legal" && !!String(form.ticketNumber || "").trim();

  // Подписи для боковой сводки заказа.
  const originName = stationOptions.find((s: any) => s.id === form.originStationID)?.name;
  const destName = stationOptions.find((s: any) => s.id === form.destinationStationID)?.name;
  const trainObj = trains.find((t: any) => t.id === form.trainID);
  const trainLabel = trainObj
    ? trainObj.route
      ? `${trainObj.number} — ${trainObj.route}`
      : trainObj.number
    : "";
  // Для юр. лица «клиентом» в сводке выступает контрагент, а не физлицо.
  const customerLabel =
    form.clientType === "legal"
      ? selectedCounterparty
        ? counterpartyLabel(selectedCounterparty)
        : "—"
      : selectedCustomer
        ? customerName(selectedCustomer)
        : "—";

  // Предварительный расчёт с сервера (источник истины по формуле — бэкенд).
  const { data: breakdown } = useQuery({
    queryKey: [
      "baggage-quote",
      {
        orgID,
        weightNum,
        qtyNum,
        declaredValue,
        hasTicket,
        originStationID: form.originStationID,
        destinationStationID: form.destinationStationID,
        departureDate: form.departureDate,
        counterpartyID: form.clientType === "legal" ? form.counterpartyID : null,
      },
    ],
    queryFn: async () => {
      const res: any = await baggageTripsService.quote({
        totalWeight: weightNum,
        pieceCount: qtyNum,
        declaredValue,
        ticketNumber: hasTicket ? "x" : "",
        // Ставка за 10 кг зависит от маршрута и месяца отправки.
        originStationID: form.originStationID || null,
        destinationStationID: form.destinationStationID || null,
        departureDate: form.departureDate || null,
        // Договорной тариф юр. лица — чтобы превью совпало с итогом.
        counterpartyID: form.clientType === "legal" ? form.counterpartyID : null,
      });
      return res.data;
    },
    enabled: weightNum > 0 && qtyNum > 0,
    placeholderData: keepPreviousData, // не моргать пустотой при пересчёте
  });

  const step0Valid =
    !!form.originStationID &&
    !!form.destinationStationID &&
    (form.clientType === "legal" ? !!form.counterpartyID : !!form.customerID);
  const step1Valid = weightNum > 0 && qtyNum >= 1;

  const canNext = activeStep === 0 ? step0Valid : activeStep === 1 ? step1Valid : true;

  // Открыть диалог создания клиента, предзаполнив ФИО из строки поиска.
  const openCreateClient = (text: string) => {
    const parts = (text || "").trim().split(/\s+/);
    setNewClient({
      open: true,
      lastName: parts[0] || "",
      firstName: parts.slice(1).join(" ") || "",
      iin: "",
      phone: "",
    });
  };

  const newClientValid =
    !!newClient.lastName.trim() &&
    !!newClient.firstName.trim() &&
    /^\d{12}$/.test(newClient.iin.trim());

  // Быстрое создание клиента (ИИН — единственное обязательное поле на бэкенде; по нему же дедуп).
  const handleCreateClient = async () => {
    if (!newClientValid) return;
    setCreatingClient(true);
    try {
      const fd = new FormData();
      fd.append("lastName", newClient.lastName.trim());
      fd.append("firstName", newClient.firstName.trim());
      fd.append("IDCard.iin", newClient.iin.trim());
      if (newClient.phone.trim()) {
        fd.append("user.phone", newClient.phone.trim());
        fd.append("user.phonePrefix", "7");
      }
      const res: any = await customersService.post(fd);
      // Контроллер возвращает { customer, ... } либо самого клиента — берём, что есть.
      const created = res?.data?.customer || res?.data;
      // Имя/фамилия обновляются на бэкенде после возврата объекта — подмешиваем введённые,
      // чтобы метка в автокомплите отобразилась сразу.
      const merged = {
        ...created,
        lastName: newClient.lastName.trim(),
        firstName: newClient.firstName.trim(),
      };
      setSelectedCustomer(merged);
      set("customerID", created?.id || null);
      setNewClient(emptyNewClient());
      snack("Клиент создан");
    } catch (ex) {
      console.error(ex);
      snack("Не удалось создать клиента");
    } finally {
      setCreatingClient(false);
    }
  };

  const handleCheckout = async () => {
    setSubmitting(true);
    try {
      const payload = {
        clientType: form.clientType,
        customerID: form.customerID,
        // Реквизиты юр. лица берёт бэкенд из выбранного контрагента.
        counterpartyID: form.clientType === "legal" ? form.counterpartyID : null,
        recipientName: form.hasRecipient ? form.recipientName : null,
        recipientPhone: form.hasRecipient ? form.recipientPhone : null,
        trainID: form.trainID || null,
        originStationID: form.originStationID,
        destinationStationID: form.destinationStationID,
        departureDate: form.departureDate,
        // Номер билета (скидка) — только для физлиц.
        ticketNumber: form.clientType === "legal" ? null : form.ticketNumber || null,
        declaredValue,
        declaredValueAmount: declaredValue ? Number(form.declaredValueAmount) : null,
        cargo: {
          weight: weightNum,
          quantity: qtyNum,
          type: form.type,
          description: form.description || null,
        },
        payment: { method: form.paymentMethod },
      };
      const res: any = await baggageTripsService.checkout(payload);
      setCreatedTrip(res.data);
      snack("Оплата подтверждена, документы сформированы");
    } catch (ex) {
      console.error(ex);
      snack("Не удалось оформить отправку");
    } finally {
      setSubmitting(false);
    }
  };

  const openBlob = async (loader: Promise<any>) => {
    const res: any = await loader;
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
  };

  const resetWizard = () => {
    setForm(initialForm());
    setSelectedCustomer(null);
    setCustomerSearch("");
    setCreatedTrip(null);
    setActiveStep(0);
  };

  // ---- Экран успеха ----
  if (createdTrip) {
    const currency = createdTrip.currency || breakdown?.currency || "KZT";
    const paymentLabel = form.paymentMethod === "kaspi" ? "Kaspi QR" : "Наличные";
    const hasBreakdown = breakdown && breakdown.total > 0;

    const PriceRow = ({
      label,
      value,
      strong,
    }: {
      label: string;
      value: string;
      strong?: boolean;
    }) => (
      <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.25 }}>
        <Typography variant="body2" color={strong ? "text.primary" : "text.secondary"}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 500 }}>
          {value}
        </Typography>
      </Box>
    );

    return (
      <Box sx={{ maxWidth: 600, mx: "auto" }}>
        <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          {/* Шапка с подтверждением */}
          <Box sx={{ bgcolor: "success.main", color: "#fff", textAlign: "center", py: 3.5, px: 2 }}>
            <MdCheckCircle size={56} />
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
              Отправка оформлена
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Документы сформированы и готовы к печати
            </Typography>
          </Box>

          <CardContent>
            {/* Номер отправки + итоговая сумма */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Номер отправки
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  №{createdTrip.id}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Итого оплачено
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "success.main" }}>
                  {money(createdTrip.totalPrice, currency)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Маршрут */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Откуда
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                  {originName || "—"}
                </Typography>
              </Box>
              <Box sx={{ color: "primary.main", display: "flex", fontSize: 22 }}>
                <MdArrowForward />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Куда
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                  {destName || "—"}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Детали отправки */}
            <SummaryLine icon={<MdPerson />} label="Клиент" value={customerLabel} />
            {trainLabel ? (
              <SummaryLine icon={<MdTrain />} label="Поезд" value={trainLabel} />
            ) : null}
            <SummaryLine
              icon={<MdLuggage />}
              label="Груз"
              value={`${qtyNum} мест · ${weightNum} кг · ${form.type}`}
            />
            <SummaryLine icon={<MdPayments />} label="Оплата" value={paymentLabel} />

            {hasTicket && (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label="Применена скидка 50% по билету"
                sx={{ mt: 1, fontWeight: 600 }}
              />
            )}

            {/* Разбивка стоимости */}
            {hasBreakdown && (
              <>
                <Divider sx={{ my: 2 }} />
                <PriceRow
                  label={`Перевозка (${breakdown.blocks} × 10 кг)`}
                  value={money(breakdown.carriage, currency)}
                />
                {breakdown.discountAmount > 0 && (
                  <PriceRow
                    label={`Скидка по билету (−${breakdown.discountPercent}%)`}
                    value={`−${money(breakdown.discountAmount, currency)}`}
                  />
                )}
                {breakdown.declaredValueFee > 0 && (
                  <PriceRow
                    label="Объявленная ценность"
                    value={money(breakdown.declaredValueFee, currency)}
                  />
                )}
                <PriceRow
                  label="Распечатка накладной"
                  value={money(breakdown.waybillFee, currency)}
                />
                <Divider sx={{ my: 1 }} />
                <PriceRow label="Итого" value={money(createdTrip.totalPrice, currency)} strong />
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Документы и переходы */}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={<MdLuggage />}
                onClick={() => openBlob(baggageTripsService.getLabelsPdf(createdTrip.id))}
              >
                Печать бирки
              </Button>
              <Button
                variant="outlined"
                startIcon={<MdReceiptLong />}
                onClick={() => openBlob(baggageTripsService.getReceiptPdf(createdTrip.id))}
              >
                Квитанция
              </Button>
              <Button
                variant="outlined"
                startIcon={<MdOpenInNew />}
                onClick={() => navigate(`/admin/baggages/baggage-trips/detail/${createdTrip.id}`)}
              >
                Открыть отправку
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Нижние действия */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2, flexWrap: "wrap" }}>
          <Button variant="text" startIcon={<MdAdd />} onClick={resetWizard}>
            Новая отправка
          </Button>
          <Button
            variant="text"
            startIcon={<MdArrowBack />}
            onClick={() => navigate("/admin/baggages/baggage-trips")}
          >
            К списку отправок
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <div>
      <div className="flex gap-2 items-center m-b-16 justify-between">
        <PageTitle>Новая отправка</PageTitle>
        <div>
          <Button
            variant="outlined"
            onClick={() => {
              navigate("/admin/baggages/baggage-trips");
            }}
          >
            Назад
          </Button>
        </div>
      </div>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* Левая колонка — шаги и форма */}
        <Box
          sx={{ flex: "1 1 520px", minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Stepper
            activeStep={activeStep}
            sx={{
              "& .MuiStepIcon-root.Mui-active": { color: "primary.main" },
              "& .MuiStepIcon-root.Mui-completed": { color: "primary.main" },
              "& .MuiStepLabel-label.Mui-active": { fontWeight: 700 },
              "& .MuiStepLabel-label.Mui-completed": { fontWeight: 600 },
            }}
          >
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              {/* Шаг 1: клиент + маршрут */}
              {activeStep === 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <SectionTitle>Информация о клиенте</SectionTitle>

                  <FormControl>
                    <FormLabel>Тип клиента</FormLabel>
                    <RadioGroup
                      row
                      value={form.clientType}
                      onChange={(e) => {
                        const v = e.target.value;
                        set("clientType", v);
                        if (v === "legal") {
                          // У юр. лица клиент-физлицо не вводится — реквизиты берутся с контрагента,
                          // и скидка по билету не применяется (тариф договорной).
                          set("customerID", null);
                          setSelectedCustomer(null);
                          set("ticketNumber", "");
                        } else {
                          set("counterpartyID", null);
                        }
                      }}
                    >
                      <FormControlLabel
                        value="individual"
                        control={<Radio />}
                        label="Физическое лицо"
                      />
                      <FormControlLabel
                        value="legal"
                        control={<Radio />}
                        label="Юридическое лицо"
                      />
                    </RadioGroup>
                  </FormControl>

                  {form.clientType === "individual" && (
                    <Autocomplete
                      options={customerOptions}
                      filterOptions={(options, params) => {
                        const filtered = customerFilter(options, params);
                        const input = params.inputValue.trim();
                        if (input !== "") {
                          filtered.push({
                            id: "__create__",
                            inputValue: input,
                            name: `+ Создать клиента «${input}»`,
                          });
                        }
                        return filtered;
                      }}
                      getOptionLabel={(c: any) => (c?.id === "__create__" ? c.name : customerName(c))}
                      value={selectedCustomer}
                      isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                      onChange={(_e, val: any) => {
                        if (val?.id === "__create__") {
                          openCreateClient(val.inputValue);
                          return;
                        }
                        setSelectedCustomer(val);
                        set("customerID", val?.id || null);
                      }}
                      onInputChange={(_e, val) => setCustomerSearch(val || "")}
                      renderOption={(props, option: any) => (
                        <li {...props} key={option.id}>
                          {option.id === "__create__" ? option.name : customerName(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Клиент"
                          required
                          helperText="Начните вводить ФИО — выберите из базы или создайте нового"
                        />
                      )}
                    />
                  )}

                  {form.clientType === "legal" && (
                    <Autocomplete
                      options={counterpartyOptions}
                      value={selectedCounterparty}
                      getOptionLabel={(c: any) => counterpartyLabel(c)}
                      isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                      onChange={(_e, val: any) => set("counterpartyID", val?.id || null)}
                      noOptionsText="Контрагенты не найдены — добавьте их в разделе «Контрагенты»"
                      renderOption={(props, option: any) => (
                        <li {...props} key={option.id}>
                          {counterpartyLabel(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Контрагент"
                          required
                          helperText="Выберите юр. лицо из реестра «Контрагенты»"
                        />
                      )}
                    />
                  )}

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.hasRecipient}
                        onChange={(e) => set("hasRecipient", e.target.checked)}
                      />
                    }
                    label="Получатель — другой человек"
                  />
                  {form.hasRecipient && (
                    <>
                      <TextField
                        label="ФИО получателя"
                        value={form.recipientName}
                        onChange={(e) => set("recipientName", e.target.value)}
                      />
                      <TextField
                        label="Телефон получателя"
                        value={form.recipientPhone}
                        onChange={(e) => set("recipientPhone", e.target.value)}
                      />
                    </>
                  )}

                  <Divider sx={{ my: 1 }} />

                  <SectionTitle>Маршрут</SectionTitle>

                  <FormControl fullWidth>
                    <InputLabel>Поезд</InputLabel>
                    <Select
                      label="Поезд"
                      value={form.trainID}
                      onChange={(e) => set("trainID", e.target.value)}
                    >
                      <MenuItem value="">—</MenuItem>
                      {trains.map((t: any) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.route ? `${t.number} — ${t.route}` : t.number}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <div className="flex gap-2">
                    <FormControl fullWidth>
                      <InputLabel>Станция отправления</InputLabel>
                      <Select
                        label="Станция отправления"
                        value={form.originStationID}
                        onChange={(e) => set("originStationID", e.target.value)}
                      >
                        {stationOptions.map((s: any) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Станция назначения</InputLabel>
                      <Select
                        label="Станция назначения"
                        value={form.destinationStationID}
                        onChange={(e) => set("destinationStationID", e.target.value)}
                      >
                        {stationOptions.map((s: any) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  <TextField
                    label="Дата отправления"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={form.departureDate}
                    onChange={(e) => set("departureDate", e.target.value)}
                  />
                </Box>
              )}

              {/* Шаг 2: груз */}
              {activeStep === 1 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <SectionTitle>Детали груза</SectionTitle>

                  <div className="flex gap-2">
                    <TextField
                      label="Вес груза, кг (всего)"
                      type="number"
                      required
                      inputProps={{ min: 0, step: 0.1 }}
                      value={form.weight}
                      onChange={(e) => set("weight", e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Количество мест"
                      type="number"
                      required
                      inputProps={{ min: 1, step: 1 }}
                      value={form.quantity}
                      onChange={(e) => set("quantity", e.target.value)}
                      fullWidth
                    />
                  </div>

                  <FormControl fullWidth>
                    <InputLabel>Упаковка</InputLabel>
                    <Select
                      label="Упаковка"
                      value={form.type}
                      onChange={(e) => set("type", e.target.value)}
                    >
                      {BAGGAGE_TYPE_OPTIONS.map((o) => (
                        <MenuItem key={o.id} value={o.id}>
                          {o.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Объявленная ценность, ₸ (необязательно)"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={form.declaredValueAmount}
                    onChange={(e) => set("declaredValueAmount", e.target.value)}
                    helperText="При указании добавляется фиксированный сбор за объявленную ценность."
                  />

                  {form.clientType !== "legal" && (
                    <TextField
                      label="Номер билета (даёт скидку 50% на перевозку)"
                      value={form.ticketNumber}
                      onChange={(e) => set("ticketNumber", e.target.value)}
                      helperText="Если у клиента есть билет — укажите номер, применится скидка 50% на перевозку."
                    />
                  )}

                  <TextField
                    label="Описание груза (необязательно)"
                    multiline
                    minRows={2}
                    value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                  />
                </Box>
              )}

              {/* Шаг 3: оплата */}
              {activeStep === 2 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <SectionTitle>Оплата</SectionTitle>

                  <FormControl>
                    <FormLabel>Способ оплаты</FormLabel>
                    <RadioGroup
                      value={form.paymentMethod}
                      onChange={(e) => set("paymentMethod", e.target.value)}
                    >
                      <FormControlLabel
                        value="kaspi"
                        control={<Radio />}
                        label={
                          <span className="flex items-center gap-2">
                            <MdQrCode2 /> Kaspi QR
                          </span>
                        }
                      />
                      <FormControlLabel
                        value="cash"
                        control={<Radio />}
                        label={
                          <span className="flex items-center gap-2">
                            <MdPayments /> Наличные
                          </span>
                        }
                      />
                    </RadioGroup>
                  </FormControl>

                  {form.paymentMethod === "kaspi" && (
                    <Card variant="outlined" sx={{ borderRadius: 2, textAlign: "center", py: 3 }}>
                      <div className="flex flex-col items-center gap-2">
                        <img
                          src="/media/images/qr.png"
                          alt="Kaspi QR"
                          style={{ width: 140, height: 140 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Отсканируйте код через Kaspi приложения
                        </Typography>
                      </div>
                    </Card>
                  )}

                  <Button
                    variant="contained"
                    size="large"
                    disabled={submitting}
                    onClick={handleCheckout}
                  >
                    {submitting
                      ? "Оформляем..."
                      : `Подтвердить оплату · ${money(breakdown?.total, breakdown?.currency || "KZT")}`}
                  </Button>
                </Box>
              )}

              {/* Навигация по шагам */}
              {activeStep < 2 && (
                <>
                  <Divider sx={{ my: 2.5 }} />
                  <div className="flex gap-2">
                    <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>
                      Назад
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!canNext}
                      onClick={() => setActiveStep((s) => s + 1)}
                    >
                      Далее
                    </Button>
                  </div>
                </>
              )}
              {activeStep === 2 && (
                <>
                  <Divider sx={{ my: 2.5 }} />
                  <div className="flex gap-2">
                    <Button onClick={() => setActiveStep((s) => s - 1)}>Назад</Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Правая колонка — сводка заказа */}
        <Box sx={{ flex: "1 1 300px", minWidth: 280, maxWidth: 380 }}>
          <OrderSummary
            originName={originName}
            destName={destName}
            customerLabel={customerLabel}
            trainLabel={trainLabel}
            weight={weightNum}
            qty={qtyNum}
            breakdown={breakdown}
            hasTicket={hasTicket}
          />
        </Box>
      </Box>

      {/* Быстрое создание клиента */}
      <Dialog
        open={newClient.open}
        onClose={() => setNewClient(emptyNewClient())}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Новый клиент</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Фамилия"
            required
            value={newClient.lastName}
            onChange={(e) => setNewClient((s) => ({ ...s, lastName: e.target.value }))}
            sx={{ mt: 1 }}
          />
          <TextField
            label="Имя"
            required
            value={newClient.firstName}
            onChange={(e) => setNewClient((s) => ({ ...s, firstName: e.target.value }))}
          />
          <TextField
            label="ИИН (12 цифр)"
            required
            value={newClient.iin}
            onChange={(e) =>
              setNewClient((s) => ({ ...s, iin: e.target.value.replace(/\D/g, "").slice(0, 12) }))
            }
            error={!!newClient.iin && !/^\d{12}$/.test(newClient.iin)}
            helperText="Используется для идентификации клиента (без дублей)."
          />
          <TextField
            label="Телефон (необязательно)"
            value={newClient.phone}
            onChange={(e) => setNewClient((s) => ({ ...s, phone: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewClient(emptyNewClient())}>Отмена</Button>
          <Button
            variant="contained"
            disabled={!newClientValid || creatingClient}
            onClick={handleCreateClient}
          >
            {creatingClient ? "Создаём..." : "Создать"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BaggageTripWizard;
